import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import nacmeLogo from './assets/nacme_logo.jpg';
import tlaLogo from './assets/tla_logo.png';
import { IS_TLA, PATHS } from './utils/platformPaths';

const BRAND_NAME = IS_TLA ? 'TLA Engineer Network' : 'NACME Searchlight';

const AlumniRegistration = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Step 1: Basic Info
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    phone: '',
    
    // Step 2: Education
    graduation_year: '',
    degree_type: '',
    major: '',
    university: '',
    
    // Step 3: Professional Info
    current_employer: '',
    current_position: '',
    years_experience: '',
    linkedin_url: '',
    
    // User Agreement
    agreementAccepted: false,
    smsOptIn: false
  });
  
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const degreeTypes = [
    { value: '', label: 'Select Degree Type' },
    { value: 'bachelors', label: "Bachelor's Degree" },
    { value: 'masters', label: "Master's Degree" },
    { value: 'phd', label: 'Ph.D.' },
    { value: 'jd', label: 'J.D. (Juris Doctor)' },
    { value: 'mba', label: 'M.B.A. (Master of Business Administration)' },
    { value: 'edd', label: 'Ed.D. (Doctor of Education)' },
    { value: 'md', label: 'M.D. (Doctor of Medicine)' },
    { value: 'deng', label: 'D.Eng. (Doctor of Engineering)' }
  ];

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData.email) newErrors.email = 'Email is required';
      if (!formData.password) newErrors.password = 'Password is required';
      if (formData.password && formData.password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      }
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
      if (!formData.first_name) newErrors.first_name = 'First name is required';
      if (!formData.last_name) newErrors.last_name = 'Last name is required';
    }

    if (step === 3) {
      if (!formData.agreementAccepted) {
        newErrors.agreementAccepted = 'You must accept the User Agreement to continue';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    // Clear error for this field
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors({});

    try {
      // Prepare data for API
      const submitData = { ...formData };
      delete submitData.confirmPassword;
      
      // Convert years_experience to number if provided
      if (submitData.years_experience) {
        submitData.years_experience = parseInt(submitData.years_experience);
      }
      
      // Convert graduation_year to number if provided
      if (submitData.graduation_year) {
        submitData.graduation_year = parseInt(submitData.graduation_year);
      }

      // Remove empty optional fields and non-API fields
      Object.keys(submitData).forEach(key => {
        if (submitData[key] === '' || key === 'agreementAccepted') {
          delete submitData[key];
        }
      });

      // Send sms_opt_in as snake_case for the API
      if (submitData.smsOptIn !== undefined) {
        submitData.sms_opt_in = submitData.smsOptIn;
        delete submitData.smsOptIn;
      }

      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      const response = await fetch(`${backendUrl}/api/alumni/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Registration failed');
      }

      // Store token and user data
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('userRole', 'alumni');
      localStorage.setItem('alumniData', JSON.stringify(data.user));

      // Show success message
      alert('Registration successful! Please check your email to verify your account.');
      
      // Navigate to alumni dashboard (platform-aware)
      navigate(PATHS.dashboard);
    } catch (err) {
      console.error('Registration error:', err);
      setErrors({ submit: err.message || 'Registration failed. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const renderProgressBar = () => {
    const steps = ['Basic Info', 'Education', 'Professional'];
    return (
      <div className="mb-6 sm:mb-8">
        <div className="flex flex-col items-center">
          <div className="flex items-center justify-center space-x-4 sm:space-x-6">
            {steps.map((step, index) => (
              <div key={index} className="flex items-center">
                <div className={`flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 ${
                  currentStep > index + 1 
                    ? 'bg-blue-600 border-blue-600 text-white'
                    : currentStep === index + 1
                    ? 'border-blue-600 text-blue-600'
                    : 'border-gray-300 text-gray-400'
                }`}>
                  {currentStep > index + 1 ? (
                    <svg className="w-4 h-4 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <span className="text-xs sm:text-sm font-medium">{index + 1}</span>
                  )}
                </div>
                {index < steps.length - 1 && (
                  <div className={`w-8 sm:w-12 h-0.5 ml-2 ${
                    currentStep > index + 1 ? 'bg-blue-600' : 'bg-gray-300'
                  }`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex items-center justify-center space-x-4 sm:space-x-6 mt-2">
            {steps.map((step, index) => (
              <div key={index} className="flex items-center">
                <div className="w-8 sm:w-10 flex justify-center">
                  <span className={`text-xs sm:text-sm text-center ${
                    currentStep === index + 1 ? 'text-blue-600 font-medium' : 'text-gray-400'
                  }`}>
                    {step}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div className="w-8 sm:w-12 ml-2" />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-6 px-4 sm:py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <img
          className={`mx-auto w-auto ${IS_TLA ? 'h-48 sm:h-60' : 'h-16 sm:h-20'}`}
          src={IS_TLA ? tlaLogo : nacmeLogo}
          alt={IS_TLA ? "TLA Logo" : "NACME Logo"}
        />
        <h2 className="mt-4 sm:mt-6 text-center text-2xl sm:text-3xl font-extrabold text-gray-900">
          {IS_TLA ? 'Create Your Account' : 'Alumni Registration'}
        </h2>
        <p className="mt-2 text-center text-sm sm:text-base text-gray-600 px-2">
          {IS_TLA ? 'Join the TLA Engineer Network' : 'Join the NACME alumni network'}
        </p>
      </div>

      <div className="mt-6 sm:mt-8 mx-auto w-full max-w-sm sm:max-w-md">
        <div className="bg-white py-6 px-4 shadow-md rounded-lg sm:py-8 sm:px-10 sm:shadow-lg">
          {renderProgressBar()}
          
          {errors.submit && (
            <div className="mb-4 rounded-md bg-red-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">
                    {errors.submit}
                  </h3>
                </div>
              </div>
            </div>
          )}

          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* Step 1: Basic Information */}
            {currentStep === 1 && (
              <>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    id="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className={`mt-1 appearance-none block w-full px-3 py-2 border ${
                      errors.email ? 'border-red-300' : 'border-gray-300'
                    } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                  />
                  {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                      Password *
                    </label>
                    <input
                      type="password"
                      name="password"
                      id="password"
                      required
                      value={formData.password}
                      onChange={handleChange}
                      className={`mt-1 appearance-none block w-full px-3 py-3 sm:py-2 border ${
                        errors.password ? 'border-red-300' : 'border-gray-300'
                      } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-base sm:text-sm`}
                    />
                    {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password}</p>}
                  </div>

                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                      Confirm Password *
                    </label>
                    <input
                      type="password"
                      name="confirmPassword"
                      id="confirmPassword"
                      required
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className={`mt-1 appearance-none block w-full px-3 py-3 sm:py-2 border ${
                        errors.confirmPassword ? 'border-red-300' : 'border-gray-300'
                      } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-base sm:text-sm`}
                    />
                    {errors.confirmPassword && <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="first_name" className="block text-sm font-medium text-gray-700">
                      First Name *
                    </label>
                    <input
                      type="text"
                      name="first_name"
                      id="first_name"
                      required
                      value={formData.first_name}
                      onChange={handleChange}
                      className={`mt-1 appearance-none block w-full px-3 py-3 sm:py-2 border ${
                        errors.first_name ? 'border-red-300' : 'border-gray-300'
                      } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-base sm:text-sm`}
                    />
                    {errors.first_name && <p className="mt-1 text-sm text-red-600">{errors.first_name}</p>}
                  </div>

                  <div>
                    <label htmlFor="last_name" className="block text-sm font-medium text-gray-700">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      name="last_name"
                      id="last_name"
                      required
                      value={formData.last_name}
                      onChange={handleChange}
                      className={`mt-1 appearance-none block w-full px-3 py-3 sm:py-2 border ${
                        errors.last_name ? 'border-red-300' : 'border-gray-300'
                      } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-base sm:text-sm`}
                    />
                    {errors.last_name && <p className="mt-1 text-sm text-red-600">{errors.last_name}</p>}
                  </div>
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    id="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(555) 123-4567"
                    className="mt-1 appearance-none block w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-base sm:text-sm"
                  />
                </div>
              </>
            )}

            {/* Step 2: Education */}
            {currentStep === 2 && (
              <>
                <div>
                  <label htmlFor="university" className="block text-sm font-medium text-gray-700">
                    University
                  </label>
                  <input
                    type="text"
                    name="university"
                    id="university"
                    value={formData.university}
                    onChange={handleChange}
                    placeholder="Your university name"
                    className="mt-1 appearance-none block w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-base sm:text-sm"
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="degree_type" className="block text-sm font-medium text-gray-700">
                      Degree Type
                    </label>
                    <select
                      name="degree_type"
                      id="degree_type"
                      value={formData.degree_type}
                      onChange={handleChange}
                      className="mt-1 block w-full px-3 py-3 sm:py-2 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-base sm:text-sm"
                    >
                      {degreeTypes.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="graduation_year" className="block text-sm font-medium text-gray-700">
                      Graduation Year
                    </label>
                    <input
                      type="number"
                      name="graduation_year"
                      id="graduation_year"
                      value={formData.graduation_year}
                      onChange={handleChange}
                      placeholder="2020"
                      min="1950"
                      max="2030"
                      className="mt-1 appearance-none block w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-base sm:text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="major" className="block text-sm font-medium text-gray-700">
                    Major/Field of Study
                  </label>
                  <input
                    type="text"
                    name="major"
                    id="major"
                    value={formData.major}
                    onChange={handleChange}
                    placeholder="e.g., Computer Science, Mechanical Engineering"
                    className="mt-1 appearance-none block w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-base sm:text-sm"
                  />
                </div>
              </>
            )}

            {/* Step 3: Professional Information */}
            {currentStep === 3 && (
              <>
                <div>
                  <label htmlFor="current_employer" className="block text-sm font-medium text-gray-700">
                    Current Employer
                  </label>
                  <input
                    type="text"
                    name="current_employer"
                    id="current_employer"
                    value={formData.current_employer}
                    onChange={handleChange}
                    placeholder="Company name"
                    className="mt-1 appearance-none block w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-base sm:text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="current_position" className="block text-sm font-medium text-gray-700">
                    Current Position
                  </label>
                  <input
                    type="text"
                    name="current_position"
                    id="current_position"
                    value={formData.current_position}
                    onChange={handleChange}
                    placeholder="Job title"
                    className="mt-1 appearance-none block w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-base sm:text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="years_experience" className="block text-sm font-medium text-gray-700">
                    Years of Experience
                  </label>
                  <input
                    type="number"
                    name="years_experience"
                    id="years_experience"
                    value={formData.years_experience}
                    onChange={handleChange}
                    placeholder="0"
                    min="0"
                    max="50"
                    className="mt-1 appearance-none block w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-base sm:text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="linkedin_url" className="block text-sm font-medium text-gray-700">
                    LinkedIn Profile URL
                  </label>
                  <input
                    type="url"
                    name="linkedin_url"
                    id="linkedin_url"
                    value={formData.linkedin_url}
                    onChange={handleChange}
                    placeholder="https://linkedin.com/in/yourprofile"
                    className="mt-1 appearance-none block w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-base sm:text-sm"
                  />
                </div>

                {/* User Agreement Consent */}
                <div className="border-t pt-6">
                  <div className="flex items-start">
                    <div className="flex items-center h-5">
                      <input
                        type="checkbox"
                        name="agreementAccepted"
                        id="agreementAccepted"
                        checked={formData.agreementAccepted}
                        onChange={handleChange}
                        className={`h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded ${
                          errors.agreementAccepted ? 'border-red-300' : ''
                        }`}
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <label htmlFor="agreementAccepted" className="text-gray-700">
                        I have read and agree to the{' '}
                        <Link
                          to={PATHS.userAgreement}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-500 underline"
                        >
                          {IS_TLA ? 'TLA Engineer Network User Agreement' : 'NACME Alumni Network User Agreement'}
                        </Link>
                        {' '}*
                      </label>
                      {errors.agreementAccepted && (
                        <p className="mt-1 text-sm text-red-600">{errors.agreementAccepted}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* SMS Opt-In Consent (separate from User Agreement per Twilio requirements) */}
                <div className="mt-4">
                  <div className="flex items-start">
                    <div className="flex items-center h-5">
                      <input
                        type="checkbox"
                        name="smsOptIn"
                        id="smsOptIn"
                        checked={formData.smsOptIn}
                        onChange={handleChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <label htmlFor="smsOptIn" className="text-gray-700">
                        I agree to receive SMS text messages from {BRAND_NAME} regarding account notifications, application updates, opportunity alerts, and event notifications. Message frequency varies. Message & data rates may apply. Reply STOP to opt out at any time. Reply HELP for help.{' '}
                        <Link
                          to={PATHS.smsTerms}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-500 underline"
                        >
                          View SMS Terms & Conditions
                        </Link>
                      </label>
                      <p className="mt-1 text-xs text-gray-500">
                        Optional &mdash; not required for registration. You can opt in or out at any time in your account settings.
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={handleBack}
                  className="inline-flex justify-center py-3 sm:py-2 px-4 sm:px-4 border border-gray-300 rounded-md shadow-sm text-base sm:text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Back
                </button>
              )}
              
              {currentStep < 3 ? (
                <button
                  type="button"
                  onClick={handleNext}
                  className="ml-auto inline-flex justify-center py-3 sm:py-2 px-4 sm:px-4 border border-transparent rounded-md shadow-sm text-base sm:text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Next
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={isLoading || !formData.agreementAccepted}
                  className={`ml-auto inline-flex justify-center py-3 sm:py-2 px-4 sm:px-4 border border-transparent rounded-md shadow-sm text-base sm:text-sm font-medium text-white ${
                    isLoading || !formData.agreementAccepted
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                  }`}
                >
                  {isLoading ? 'Creating Account...' : 'Create Account'}
                </button>
              )}
            </div>
          </form>

          <div className="mt-6 text-center">
            <span className="text-sm text-gray-600">
              Already have an account?{' '}
              <Link to={PATHS.login} className="font-medium text-blue-600 hover:text-blue-500">
                Sign in
              </Link>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlumniRegistration;